import React from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { BRAND } from "@/lib/site";
import ContactForm from "@/components/site/ContactForm";
import Seo from "@/components/site/Seo";

const CONTACT_JSONLD = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "url": "https://yourelitehomecare.com/contact",
  "name": "Contact Elite Home Care by Hortense"
};

export default function Contact() {
  return (
    <div data-testid="page-contact">
      <Seo
        path="/contact"
        title="Contact Us — Schedule a Free In-Home Care Consultation"
        description="Schedule a free 20-minute consultation with our SWFL care coordinator. Call (239) 834-4377 or submit the form — no obligation, response within one business day. Serving Naples, Fort Myers, Cape Coral & Bonita Springs."
        jsonLd={CONTACT_JSONLD}
      />
      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2">
            <span className="overline">Contact us</span>
            <h1 className="text-5xl md:text-6xl leading-[1.05]">
              We’re here,{" "}
              <span className="italic text-gold-500">whenever you need us.</span>
            </h1>
            <p className="mt-6 text-navy-600 text-lg leading-relaxed">
              Share a few details and our care coordinator will reach out
              within one business day — or call us directly any time.
            </p>

            <div className="mt-10 space-y-6">
              <ContactInfo
                I={Phone}
                label="Call us"
                primary={BRAND.phone}
                href={BRAND.phoneHref}
                testid="contact-phone"
              />
              <ContactInfo
                I={Mail}
                label="Email"
                primary={BRAND.email}
                href={`mailto:${BRAND.email}`}
                testid="contact-email"
              />
              <ContactInfo
                I={MapPin}
                label="Service area"
                primary={BRAND.serviceArea}
                secondary={BRAND.address}
              />
              <ContactInfo
                I={Clock}
                label="Hours"
                primary={BRAND.hours}
                secondary="Care coordinators on call around the clock."
              />
            </div>
          </div>

          <div className="lg:col-span-3">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactInfo({ I, label, primary, secondary, href, testid }) {
  const inner = (
    <div className="flex items-start gap-4">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-navy-900 text-gold-300 shrink-0">
        <I className="h-5 w-5" strokeWidth={1.6} />
      </span>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-navy-500">
          {label}
        </div>
        <div className="mt-1 font-serif text-2xl text-navy-900">{primary}</div>
        {secondary && (
          <div className="mt-1 text-navy-500 text-sm">{secondary}</div>
        )}
      </div>
    </div>
  );
  if (href) {
    return (
      <a
        href={href}
        data-testid={testid}
        className="block hover:text-gold-500 transition-colors"
      >
        {inner}
      </a>
    );
  }
  return <div data-testid={testid}>{inner}</div>;
}
